import { useGoToPathFactory, useScopedPathnameFactory } from '@retailer-platform/dashboard/routing'
import { clientEnv } from '@retailer-platform/dashboard/env'
import { routes, type DomainRouteName } from '../../routing/routes'

// Matches extras/base62.rb alphabet (0-9 a-z A-Z) used in Order#receipt_link_admin
const BASE62_ALPHABET = '0123456789abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ'

const base62Encode = (numStr: string): string => {
  let n = BigInt(numStr)
  if (n === 0n) return '0'
  let result = ''
  while (n > 0n) {
    result = BASE62_ALPHABET[Number(n % 62n)] + result
    n = n / 62n
  }
  return result
}

const SHOPPERS_ADMIN_URL =
  (window.__RPP_ENV__ as Record<string, string | undefined> | undefined)
    ?.PUBLIC_CLIENT_SHOPPERS_ADMIN_URL ?? 'https://shoppers-admin.instacart.com'

const LOGISTICS_URL =
  (window.__RPP_ENV__ as Record<string, string | undefined> | undefined)
    ?.PUBLIC_CLIENT_LOGISTICS_URL ?? 'https://logistics.instacart.com'

export const useDomainScopedPathname = useScopedPathnameFactory<DomainRouteName>()
export const useDomainGoToPath = useGoToPathFactory<DomainRouteName>()

export const buildCustomerUrl = (customerId?: string) =>
  routes['app-admin-customers-and-orders-ipp-customer-details'].replace(
    ':customerId',
    customerId ?? ''
  )

export const buildOrderUrl = (customerId: string, orderId: string) =>
  routes['app-admin-customers-and-orders-ipp-customer-order-details']
    .replace(':customerId', customerId)
    .replace(':orderId', orderId)

export const buildRestaurantOrderUrl = (customerId: string, orderId: string) =>
  routes['app-admin-customers-and-orders-legacy-care-restaurant-order-details']
    .replace(':customerId', customerId)
    .replace(':orderId', orderId)

export const buildBatchUrl = (batchId: string) =>
  routes['app-admin-customers-and-orders-legacy-batch-details'].replace(':batchId', batchId)

export const buildCustomerNotesUrl = (customerId: string) =>
  `${routes['app-admin-customers-and-orders-dashboard-legacy']}/customers/${customerId}/notes`

export const buildChargeLogUrl = (chargeLogId: string) =>
  `${routes['app-admin-customers-and-orders-dashboard-legacy']}/charge_logs/${chargeLogId}`

export const buildCreditUrl = (creditId: string) =>
  `${routes['app-admin-customers-and-orders-dashboard-legacy']}/credits/${creditId}`

export const buildShopperUrl = (shopperId: string) => `${SHOPPERS_ADMIN_URL}/shoppers/${shopperId}`

export const buildShopperBatchUrl = (batchId: string) => `${SHOPPERS_ADMIN_URL}/batches/${batchId}`

export const buildDriverBatchUrl = (batchId: string) => `${LOGISTICS_URL}/batches/${batchId}`

export const buildOrderPhotosUrl = (orderId: string) =>
  `${SHOPPERS_ADMIN_URL}/order_delivery_photos/${orderId}`

export const buildOrderReceiptUrl = (orderId: string): string => {
  const baseUrl = clientEnv.PUBLIC_CLIENT_CX_ADMIN_MIGRATION_BASE_URL ?? 'https://www.instacart.com'
  return `${baseUrl}/orders/${base62Encode(orderId)}/receipt`
}

/** Returns full URL (origin + path) for copying or opening in new tab. */
export const buildOrderUrlFull = (customerId: string, orderId: string): string => {
  const path = buildOrderUrl(customerId, orderId)
  if (typeof window === 'undefined') return path
  const base = path.startsWith('/') ? path : `/${path}`
  return `${window.location.origin}${base}`
}
